import React from 'react';

import { UserData } from '@client/store/globalStore/state/initialGlobalState';
import i18n from '@client/utils/i18n';
import { ProjectStatus } from '@ledium/types';
import { CircadianChart, RecipeSections, WavelengthChart } from '@ledium/ui-common';
import FastForwardIcon from '@mui/icons-material/FastForward';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import StopIcon from '@mui/icons-material/Stop';
import { Checkbox, Container, Grid, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

import { DashboardProjectItem } from './DashboardProject';
import DliInfoPopper, { SectionDliInfo } from './DliInfoPopper';
import ProjectTime from './ProjectTime';

interface DashboardProjectViewProps {
    project: DashboardProjectItem;
    projectStatus: Partial<ProjectStatus>;
    chartData: Record<'pfd' | 'irradiance', Parameters<typeof CircadianChart>[0]['data']>;
    spectrumChartData: Partial<{
        chart: Array<{ waveLength: number; pfd: number; irradiance: number }>;
        maxIntensity: number;
        maxIrradianceIntensity: number;
        channelIrradiance: Record<string, number>;
    }>;
    startProject: (startTime: number | null) => void;
    stopProject: () => void;
    pauseProject: () => void;
    userData: UserData;
    jumpSection: (direction: 'back' | 'restart' | 'forward') => void;
    jumpWorking: boolean;
    currentDli: number | null;
    sectionDlis: SectionDliInfo[] | null;
    sectionDlisLoading: boolean;
    onRequestSectionDlis: () => void;
}

const DashboardProjectView = ({
    project,
    projectStatus,
    chartData,
    startProject,
    stopProject,
    pauseProject,
    userData,
    jumpSection,
    jumpWorking,
    spectrumChartData,
    currentDli,
    sectionDlis,
    sectionDlisLoading,
    onRequestSectionDlis
}: DashboardProjectViewProps) => {
    const nextTenMinWindowTs = Math.floor(+new Date() / 600000) * 600000 + 600000;
    const [delayCheck, setDelayCheck] = React.useState(false);
    const [startTime, setStartTime] = React.useState<Dayjs>(dayjs(nextTenMinWindowTs));
    const [unit, setUnit] = React.useState<'pfd' | 'irradiance'>('pfd');
    let isDelayed = false;

    if (project.running) {
        isDelayed = Date.now() / 1000 - project.startTime! < 0;
    }

    return (
        <Paper sx={{ pb: 2 }} elevation={3}>
            <Container>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="h2">{`${i18n('project')}: ${project.name}`}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1">{project.description}</Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <RecipeSections
                            status={project.running ? projectStatus.section : project.pauseData && project.pauseData.pauseStatus.section}
                            sectionList={project.recipe?.sectionList ?? []}
                            passive={true}
                        />
                    </Grid>

                    {project.running ? (
                        <>
                            <Grid item xs={10}>
                                {isDelayed
                                    ? i18n('project_is_starts_with_a_delay_within', <ProjectTime startTime={project.startTime!} />)
                                    : i18n('project_is_running_for', <ProjectTime startTime={project.startTime!} />)}

                                {projectStatus.section?.currentRepeatNum && !isDelayed ? (
                                    <div>
                                        {i18n(
                                            'project_detailed_status',
                                            <b>{projectStatus.section.name}</b>,
                                            <b>{projectStatus.section.currentRepeatNum}</b>,
                                            <b>{projectStatus.section.repeatNum}</b>,
                                            <b>{projectStatus.section.sectionPercent}</b>
                                        )}
                                    </div>
                                ) : null}
                                {currentDli !== null && !isDelayed ? (
                                    <div>
                                        {i18n('dli')}
                                        {': '}
                                        <DliInfoPopper
                                            currentDli={currentDli}
                                            projectStatus={projectStatus}
                                            sectionDlis={sectionDlis}
                                            sectionDlisLoading={sectionDlisLoading}
                                            onOpen={onRequestSectionDlis}
                                        />
                                    </div>
                                ) : null}
                            </Grid>

                            {projectStatus?.channels?.length && !isDelayed && (
                                <Grid item xs={12} md={6}>
                                    <ToggleButtonGroup
                                        color="primary"
                                        value={unit}
                                        exclusive
                                        size="small"
                                        onChange={function (_e, value) {
                                            if (value !== null) {
                                                setUnit(value);
                                            }
                                        }}
                                        sx={{ mb: 1 }}
                                    >
                                        <ToggleButton value="pfd">{i18n('pfd')}</ToggleButton>
                                        <ToggleButton value="irradiance">{i18n('irradiance')}</ToggleButton>
                                    </ToggleButtonGroup>
                                    <p>
                                        {i18n('current_intensity')}
                                        {': '}
                                        <b>
                                            {unit === 'pfd'
                                                ? projectStatus.channels.reduce((a, c) => a + c.currentPfd, 0)
                                                : projectStatus.channels.reduce((a, c) => a + (spectrumChartData.channelIrradiance?.[c.id] ?? 0), 0)}
                                        </b>{' '}
                                        {i18n(unit + '_unit')}
                                    </p>
                                    <table style={{ borderCollapse: 'collapse', fontSize: '13px' }}>
                                        <tbody>
                                            <tr>
                                                <th style={{ border: '1px solid rgba(0,0,0,0.2)', padding: '4px 8px' }}>{i18n('light_channel')}</th>
                                                <th style={{ border: '1px solid rgba(0,0,0,0.2)', padding: '4px 8px' }}>{i18n('dmx_channel')}</th>
                                                <th style={{ border: '1px solid rgba(0,0,0,0.2)', padding: '4px 8px' }}>
                                                    {i18n('intensity')} {i18n(unit + '_unit')}
                                                </th>
                                                <th style={{ border: '1px solid rgba(0,0,0,0.2)', padding: '4px 8px' }}>{i18n('value')}</th>
                                            </tr>
                                            {projectStatus.channels.map((channel, idx) => (
                                                <tr key={idx}>
                                                    <td style={{ border: '1px solid rgba(0,0,0,0.2)', padding: '4px 8px' }}>{channel.name}</td>
                                                    <td style={{ border: '1px solid rgba(0,0,0,0.2)', padding: '4px 8px' }} align="center">
                                                        <b>{channel.channelId}</b>, {i18n('X_bit', channel.resolution)}
                                                    </td>
                                                    <td style={{ border: '1px solid rgba(0,0,0,0.2)', padding: '4px 8px' }} align="right">
                                                        <b>{unit === 'pfd' ? channel.currentPfd : (spectrumChartData.channelIrradiance?.[channel.id] ?? 0)}</b>{' '}
                                                        {i18n(unit + '_unit')}
                                                    </td>
                                                    <td style={{ border: '1px solid rgba(0,0,0,0.2)', padding: '4px 8px' }} align="right">
                                                        {channel.resolution > 1 ? channel.percent + '%' : i18n(channel.value ? 'one_bit_on' : 'one_bit_off')}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </Grid>
                            )}
                            {spectrumChartData?.chart?.length && !isDelayed ? (
                                <Grid item xs={12} md={6}>
                                    <WavelengthChart
                                        unit={unit}
                                        data={spectrumChartData.chart as Parameters<typeof WavelengthChart>[0]['data']}
                                        aspect={2}
                                        animation={200}
                                        yAxis={unit === 'pfd' ? spectrumChartData.maxIntensity : spectrumChartData.maxIrradianceIntensity}
                                    />
                                </Grid>
                            ) : null}
                            {chartData[unit].length && !isDelayed ? (
                                <Grid item xs={12}>
                                    <CircadianChart
                                        data={chartData[unit]}
                                        unit={unit}
                                        referencePercent={projectStatus.section ? parseFloat(projectStatus.section.sectionPercent) : undefined}
                                    />
                                </Grid>
                            ) : null}
                        </>
                    ) : null}

                    {project.pauseData ? (
                        <Grid item xs={12}>
                            {i18n('project_is_paused_at', <ProjectTime startTime={project.startTime!} pausedTime={project.pauseData.time} />)}
                            <div>
                                {i18n(
                                    'project_detailed_last_status',
                                    <b>{project.pauseData.pauseStatus.section.name}</b>,
                                    <b>{project.pauseData.pauseStatus.section.currentRepeatNum}</b>,
                                    <b>{project.pauseData.pauseStatus.section.repeatNum}</b>,
                                    <b>{project.pauseData.pauseStatus.section.sectionPercent}</b>
                                )}
                            </div>
                        </Grid>
                    ) : null}

                    {userData?.admin && !project.running && !project.pauseData && (
                        <>
                            <Grid item xs={12} md={6} sx={{ textAlign: 'center' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<PlayArrowIcon />}
                                    onClick={() => {
                                        setDelayCheck(false);
                                        startProject(delayCheck ? dayjs(startTime).unix() : null);
                                    }}
                                >
                                    {i18n('start_project')}
                                </Button>
                            </Grid>
                            <Grid item xs={12} md={6} sx={{ textAlign: 'center' }}>
                                <FormControlLabel
                                    onChange={(event) => {
                                        setDelayCheck((event as React.ChangeEvent<HTMLInputElement>).target.checked);
                                    }}
                                    control={<Checkbox />}
                                    label={i18n('set_start_time')}
                                />
                                {delayCheck && (
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DateTimePicker
                                            timeSteps={{ hours: 1, minutes: 10 }}
                                            ampm={false}
                                            value={startTime}
                                            onChange={(newValue) => setStartTime(newValue ?? dayjs())}
                                        />
                                    </LocalizationProvider>
                                )}
                            </Grid>
                        </>
                    )}
                    {userData?.admin && (project.running || project.pauseData) && (
                        <>
                            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                                {projectStatus && !project.pauseData && !isDelayed ? (
                                    <ButtonGroup variant="contained">
                                        {projectStatus.prevSection?.valid ? (
                                            <Tooltip
                                                title={i18n('jump_back', projectStatus.prevSection.name, projectStatus.prevSection.repeatNum)}
                                                placement="top"
                                                arrow
                                            >
                                                {/* eslint-disable-next-line*/}
                                            <Button disabled={jumpWorking} onClick={() => jumpSection('back')}><FastRewindIcon /></Button>
                                            </Tooltip>
                                        ) : (
                                            <Button disabled>
                                                <FastRewindIcon />
                                            </Button>
                                        )}
                                        <Tooltip title={i18n('restart_current_circadian')} placement="top" arrow>
                                            {/* eslint-disable-next-line*/}
                                        <Button disabled={jumpWorking} onClick={() => jumpSection('restart')}><ReplayIcon /></Button>
                                        </Tooltip>
                                        {projectStatus.nextSection?.valid ? (
                                            <Tooltip
                                                title={i18n('jump_forward', projectStatus.nextSection.name, projectStatus.nextSection.repeatNum)}
                                                placement="top"
                                                arrow
                                            >
                                                {/* eslint-disable-next-line*/}
                                            <Button disabled={jumpWorking} onClick={() => jumpSection('forward')}><FastForwardIcon /></Button>
                                            </Tooltip>
                                        ) : (
                                            <Button disabled>
                                                <FastForwardIcon />
                                            </Button>
                                        )}
                                    </ButtonGroup>
                                ) : null}
                            </Grid>
                            {!isDelayed && (
                                <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                                    <Button variant="contained" startIcon={project.pauseData ? <PlayArrowIcon /> : <PauseIcon />} onClick={pauseProject}>
                                        {project.pauseData ? i18n('resume_project') : i18n('pause_project')}
                                    </Button>
                                </Grid>
                            )}
                            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                                <Button variant="contained" color="secondary" startIcon={<StopIcon />} onClick={stopProject}>
                                    {i18n('stop_project')}
                                </Button>
                            </Grid>
                        </>
                    )}
                </Grid>
            </Container>
        </Paper>
    );
};

export default React.memo(DashboardProjectView);
